import React, { useEffect, useMemo, useRef, useState } from 'react';

import calendarSvg from '@assets/img/icons/calendar.svg';
import downDisabledIcon from '@assets/img/icons/down-disabled.svg';
import downIcon from '@assets/img/icons/down.svg';
import { InputCommonProps, shouldDisplayErrorMessages } from '@components/NextForms/common';
import { ErrorMessages } from '@components/NextForms/ErrorMessages';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Dialog from '@material-ui/core/Dialog';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import CheckIcon from '@material-ui/icons/Check';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { KeyboardDatePicker, KeyboardDatePickerProps } from '@material-ui/pickers';
import { MobxZodField, MobxZodTypes } from '@monoid-dev/mobx-zod-form';
import { getForm } from '@monoid-dev/mobx-zod-form-react';
import { unwrapNullish } from '@monoid-dev/zod-utils';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { f } from '@utils/nextForms';
import { mapOptional } from '@utils/types';
import clsx from 'clsx';
import Color from 'color';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import useEvent from 'react-use-event-hook';
import { ZodEnum, ZodString, ZodTypeAny } from 'zod';

const useStyles = makeMuiStyles((theme, common) => ({
  textInputContainer: {
    ...common.flexInline,
    backgroundColor: theme.palette.background.paper,
    borderStyle: 'solid',
    borderWidth: 1,
    borderRadius: 3,
    borderColor: theme.palette.grey[500],
    padding: '4.5px 6px',
    '&$focused': {
      borderColor: theme.palette.primary.main,
    },
    '&$disabled': {
      backgroundColor: theme.palette.grey['100'],
    },
    '&$error': {
      borderColor: theme.palette.error.main,
    },
    '&.display': {
      borderColor: 'transparent',
      backgroundColor: 'transparent',
    },
    '&.tagSearch': {
      padding: 0,
      borderWidth: 0,
      borderColor: 'transparent',
      backgroundColor: 'transparent',
    },
    transition: `border-color 0.2s ease`,
  },
  textInputInput: {
    ...common.middleText,
    borderStyle: 'none',
    borderWidth: 0,
    outline: 'none',
    minWidth: 0,
    width: '100%',
    flex: 1,
    flexShrink: 1,
    backgroundColor: 'transparent',
    // Some HTML addon can change this
    padding: 0,
    'input&': {
      lineHeight: '14px',
    },
    '&::placeholder': {
      ...common.textGray400,
    },
  },
  textInputInputTypeDisplay: {},
  selectorItemsPaper: {
    overflow: 'auto',
    maxHeight: '30vh',
  },
  menuItem: {
    ...common.middleText,
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
    '&$splitter': {
      borderBottom: `1px dashed ${theme.palette.grey[400]}`,
    },
  },
  menuListRoot: {
    minWidth: 'max-content',
  },
  selectedItem: {
    backgroundColor: `${Color(theme.palette.primary.main).alpha(0.1).toString()} !important`,
  },
  optionGroupLabel: {
    ...common.middleText,
    padding: '0.25rem 0.5rem',
    margin: '0.25rem 0.5rem',
  },
  optionGroupIndicator: {
    position: 'absolute',
    top: '0.6rem',
    left: '0.5rem',
    height: 'calc(100% - 1.2rem)',
    width: '2px',
    backgroundColor: theme.palette.primary.main,
  },
  baseIcon: {
    paddingLeft: '0.5rem',
    color: theme.palette.grey['300'],
    display: 'block',
    marginTop: -10,
    marginBottom: -10,
  },
  splitter: {},
  error: {},
  disabled: {},
  focused: {},
}));

export type TextInputCommonProps = Pick<React.ComponentProps<'input'>, 'type'> &
  Pick<React.ComponentProps<'textarea'>, 'rows'>;

export type InputOptions =
  | {
      type: 'choose';
      options?: (TextInputOptionGroup | TextInputOption)[];
    }
  | {
      type: 'autoComplete';
    }
  | {
      type: 'date';
      keyboardDatePickerProps?: Partial<KeyboardDatePickerProps>;
    }
  | { type: 'time' }
  | {
      type: 'yen';
    }
  | {
      type: 'display';
    }
  | {
      type: 'password';
    };

export interface TextInputOption {
  label: string;
  value: string | number | boolean;
  labelIcon?: React.ReactNode;
}

export interface TextInputOptionGroup {
  label: string;
  children: TextInputOption[];
}

export const flattenTextInputOptions = (
  options: (TextInputOption | TextInputOptionGroup)[],
): TextInputOption[] => {
  return options.map((o) => ('children' in o ? o.children : o)).flat();
};

export interface SelectorItemsProps {
  selectorOptions?: (TextInputOptionGroup | TextInputOption)[];
  onSelectorOptionSelect?: (option: string | number | boolean) => void;
  selectedValue?: string | number | readonly string[];
}

const SelectorItems = React.memo(
  React.forwardRef<HTMLUListElement, SelectorItemsProps>((props, ref) => {
    const { selectorOptions, onSelectorOptionSelect, selectedValue } = props;

    const classes = { ...useStyles(), ...useCommonStyles() };

    const renderItem = ({ label, value: optionValue, labelIcon }: TextInputOption) => {
      const isSelected =
        selectedValue instanceof Array
          ? selectedValue.includes(optionValue as string)
          : optionValue === selectedValue;

      return (
        <MenuItem
          selected={isSelected}
          autoFocus={false}
          key={String(optionValue)}
          className={clsx(classes.menuItem)}
          classes={{ selected: classes.selectedItem }}
          onClick={() => {
            onSelectorOptionSelect?.(optionValue);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.stopPropagation();
              e.preventDefault();
              onSelectorOptionSelect?.(optionValue);
            }
          }}
        >
          <div
            className={clsx(
              classes.flexInline,
              classes.flex1,
              classes.justifyBetween,
              classes.itemsCenter,
            )}
          >
            <span className={clsx(classes.flex1, classes.flexInline, classes.itemsCenter)}>
              <span>{label}</span>
              {labelIcon}
            </span>

            {isSelected && (
              <>
                <span style={{ width: '0.5rem' }} />
                <CheckIcon color="primary" fontSize="inherit" />
              </>
            )}
          </div>
        </MenuItem>
      );
    };

    return (
      <MenuList
        ref={ref}
        autoFocus={true}
        autoFocusItem={true}
        variant="selectedMenu"
        classes={{ root: classes.menuListRoot }}
      >
        {selectorOptions?.map((option, index) => {
          if ('children' in option) {
            return (
              <div key={index}>
                <div style={{ position: 'relative' }}>
                  <div className={classes.optionGroupIndicator} />
                  <div className={classes.optionGroupLabel}>{option.label}</div>
                </div>
                {option.children.map((childOption) => {
                  return renderItem(childOption);
                })}
              </div>
            );
          }
          return renderItem(option);
        })}
      </MenuList>
    );
  }),
);

export interface AutoCompleteOption<T> {
  label: string;
  value: T;
  labelIcon?: React.ReactNode;
  /**
   * Whether to have a dashed splitter under the option.
   * Useful to split a list of options.
   */
  splitter?: boolean;
}

export interface AutoCompleteConfig<T = any> {
  autoCompleteOptions: AutoCompleteOption<T>[];
  filter?: (value: any, options: AutoCompleteOption<T>[]) => AutoCompleteOption<T>[];
  onSelect?: (option: AutoCompleteOption<T>) => void;
}

const AutoCompleteItems = observer(
  React.forwardRef<
    HTMLUListElement,
    {
      field: MobxZodField<ZodTypeAny>;
      config: AutoCompleteConfig;
      onClose: () => void;
    }
  >(({ field, config, onClose }, ref) => {
    const classes = { ...useStyles(), ...useCommonStyles() };

    const [keySelected, setKeySelected] = useState(0);
    /**
     * Temporarily hide the autocomplete, if user has selected.
     * After the user start typing again, show again.
     */
    const [hideForClose, setHideForClose] = useState(false);

    const getMenuItemKey = (i: number) => `menu-item-${field.uniqueId}-${i}`;

    const options = useMemo(() => {
      const decoded = field.decodeResult;
      if (!decoded.success) return null;

      const filteredOptions = config.filter
        ? config.filter(decoded.data, config.autoCompleteOptions)
        : config.autoCompleteOptions.filter(
            (option) =>
              String(option.value)
                .toLocaleLowerCase()
                .indexOf(String(decoded.data).toLocaleLowerCase()) !== -1,
          );

      if (filteredOptions.length === 1 && filteredOptions[0].value === decoded.data) {
        return null;
      }

      return filteredOptions.map((option, i) => (
        <MenuItem
          id={getMenuItemKey(i)}
          key={JSON.stringify(option.value)}
          selected={i === keySelected}
          className={clsx(classes.menuItem, option.splitter && classes.splitter)}
          classes={{ selected: classes.selectedItem }}
          onClick={() => {
            if (config.onSelect) {
              config.onSelect(option);
            } else {
              field.setOutput(option.value);
            }

            onClose();
            setHideForClose(true);
          }}
        >
          <div
            className={clsx(
              classes.flexInline,
              classes.flex1,
              classes.justifyBetween,
              classes.itemsCenter,
            )}
          >
            <span className={clsx(classes.flex1, classes.flexInline, classes.itemsCenter)}>
              <span>{option.label}</span>
              {option.labelIcon}
            </span>
          </div>
        </MenuItem>
      ));
    }, [config.autoCompleteOptions, field.rawInput, keySelected]);

    const onKeyDown = useEvent(function (ev: KeyboardEvent) {
      if (ev.isComposing) return;

      setHideForClose(false);

      if (ev.key === 'Enter') {
        ev.preventDefault();
        ev.stopPropagation();

        (document.querySelector(`#` + getMenuItemKey(keySelected)) as HTMLDivElement).click();
      } else if (ev.key === 'ArrowUp' || ev.key === 'ArrowDown') {
        const offset = ev.key === 'ArrowUp' ? -1 : 1;

        ev.preventDefault();
        ev.stopPropagation();

        if (options) {
          setKeySelected((k) => (k + offset + options.length) % options.length);
        }
      }
    });

    useEffect(() => {
      document
        .querySelector(`#` + getMenuItemKey(keySelected))
        ?.scrollIntoView({ block: 'nearest' });
    }, [keySelected]);

    useEffect(() => {
      const element = field.element! as HTMLInputElement;
      element.addEventListener('keydown', onKeyDown);

      return () => element.removeEventListener('keydown', onKeyDown);
    }, []);

    return (
      <>
        {options?.length && !hideForClose ? (
          <MenuList ref={ref} autoFocus={false} classes={{ root: classes.menuListRoot }}>
            {options}
          </MenuList>
        ) : null}
      </>
    );
  }),
);

export interface PopperConfig {
  /**
   * by default behave as `full`.
   * The width of the popper is as big as the input box if `full`.
   * The width is `max-content` follows its children if `max-content`.
   */
  width?: 'full' | 'max-content';
}

export type TextInputVariant = 'text' | 'tagSearch';

export type TextInputProps<T extends MobxZodTypes> = InputCommonProps &
  TextInputCommonProps & {
    field: MobxZodField<T>;
    inputOptions?: InputOptions;
    containerProps?: React.ComponentProps<'div'>;
    inputProps?: React.ComponentProps<'input'>;
    multiline?: boolean;
    autoCompleteConfig?: AutoCompleteConfig;
    popperConfig?: PopperConfig;
    variant?: TextInputVariant;
    /**
     * When user selects from an option (instead of hand-input one)
     */
    onSelect?: () => void;
  };

export const TextInput = observer(<T extends MobxZodTypes>(props: TextInputProps<T>) => {
  const { field } = props;

  const fieldType = unwrapNullish(field.type);

  const getDefaultOptionsByType = ():
    | undefined
    | Pick<TextInputProps<T>, 'inputOptions' | 'inputProps'> => {
    if (f.isDateString(fieldType)) {
      return {
        inputOptions: {
          type: 'date',
        },
        inputProps: {
          type: 'text',
        },
      };
    } else if (f.isTimeString(fieldType)) {
      return {
        inputOptions: {
          type: 'time',
        },
        inputProps: {
          type: 'time',
        },
      };
    } else if (f.isYen(fieldType)) {
      return {
        inputOptions: {
          type: 'yen',
        },
        inputProps: {
          type: 'text',
        },
      };
    } else if (f.isSecret(fieldType)) {
      return {
        inputOptions: {
          type: 'password',
        },
        inputProps: {
          type: 'password',
        },
      };
    } else if (fieldType instanceof ZodEnum) {
      return {
        inputOptions: {
          type: 'choose',
          options: fieldType.options.map((o: any) => ({ label: o, value: o })),
        },
        inputProps: {
          type: 'text',
        },
      };
    } else if (fieldType instanceof ZodString) {
      if (fieldType.isEmail) {
        return {
          inputProps: {
            autoComplete: 'email',
          },
        };
      }
    }
  };

  const {
    disabled,
    variant = 'text',
    withErrorMessages = variant === 'tagSearch' ? false : true,
    inputOptions = getDefaultOptionsByType()?.inputOptions,
    type = 'text',
    containerProps,
    inputProps,
    multiline = false,
    autoCompleteConfig,
    popperConfig,
    rows,
    onSelect,
  } = props;

  const { valueEncoder, rawValueDecoder } = fieldType.getFormMeta();

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const hasErrors = useMemo(
    () => shouldDisplayErrorMessages(field),
    [field.errorMessages.length, field.extraErrorMessages.length, field.touched],
  );
  const [isFocused, setIsFocused] = useState(false);
  const anchorElRef = useRef<HTMLDivElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement>();
  const [passwordVisible, setPasswordVisible] = useState(false);

  const isPopupOpen = !!anchorEl;
  const onOpenPopup = () => setAnchorEl(anchorElRef.current!);
  const onClosePopup = () => setAnchorEl(undefined);

  const fieldInputProps = getForm(field).bindField(field, { type: type as any });

  const readOnly = inputOptions?.type === 'choose' || inputOptions?.type === 'display';

  const inputPropsWithDefault: React.ComponentProps<'input'> = {
    ...getDefaultOptionsByType()?.inputProps,
    ...inputProps,
    ...(autoCompleteConfig && {
      autoComplete: 'off',
    }),
    ...(inputOptions?.type === 'password' && {
      type: passwordVisible ? 'text' : 'password',
    }),
  };

  const finalInputProps = {
    readOnly,
    ...fieldInputProps,
    ...inputPropsWithDefault,
    disabled,
    className: clsx(
      classes.textInputInput,
      inputProps?.className,
      fieldInputProps.className,
      inputOptions?.type === 'choose' && classes.baseButton,
    ),
    onChange(e) {
      if (valueEncoder) {
        field.setRawInput(valueEncoder(e.target.value));
      } else {
        fieldInputProps?.onChange?.(e);
      }
      inputPropsWithDefault?.onChange?.(e);
    },
    onFocus(e) {
      fieldInputProps.onFocus?.(e);
      inputPropsWithDefault?.onFocus?.(e);
      setIsFocused(true);
    },
    onBlur(e) {
      fieldInputProps.onBlur?.(e);
      inputPropsWithDefault?.onBlur?.(e);
      setTimeout(() => {
        setIsFocused(false);
      }, 200);
    },
    get value() {
      switch (inputOptions?.type) {
        case 'choose':
          return (
            flattenTextInputOptions(inputOptions.options ?? []).find(
              (o) => field.decodeResult.success && o.value === field.decodeResult.data,
            )?.label || ''
          );
        default:
          return (
            mapOptional(rawValueDecoder, (r) => r(fieldInputProps.value)) ?? fieldInputProps.value
          );
      }
    },
  } satisfies React.ComponentProps<'input'>;

  const finalContainerProps: React.ComponentProps<'div'> = {
    ...containerProps,
    ref: anchorElRef,
    className: clsx(
      variant,
      classes.textInputContainer,
      isFocused && classes.focused,
      hasErrors && classes.error,
      disabled && classes.disabled,
      inputOptions?.type === 'choose' && !disabled && classes.baseButton,
      inputOptions?.type,
    ),
    onClick: () => {
      if (!anchorEl && anchorElRef.current && !disabled && inputOptions?.type !== 'date') {
        onOpenPopup();
      }
    },
  };

  const renderPopper = (children: React.ReactNode, type: 'click' | 'focus') => {
    return (
      <Popper
        open={type === 'click' ? isPopupOpen : isFocused}
        anchorEl={() => anchorElRef.current!}
        role="menu"
        popperOptions={{ placement: 'bottom-start' }}
        style={{
          width:
            popperConfig?.width == 'max-content'
              ? 'max-content'
              : (anchorElRef.current?.clientWidth ?? 0) + 2,
          zIndex: 99999999,
        }}
      >
        <Paper className={classes.selectorItemsPaper}>
          <ClickAwayListener onClickAway={() => type === 'click' && onClosePopup()}>
            {children}
          </ClickAwayListener>
        </Paper>
      </Popper>
    );
  };

  useEffect(() => {
    if (variant === 'tagSearch') {
      onOpenPopup();
    }
  }, [variant]);

  return (
    <>
      <div {...finalContainerProps}>
        <>
          {inputOptions?.type === 'display' && (
            <div className={clsx(finalInputProps.className, classes.whitespacePreWrap)}>
              {finalInputProps.value || ' '}
            </div>
          )}

          {inputOptions?.type !== 'display' && (
            <>
              {!multiline && <input {...finalInputProps} />}
              {multiline && <textarea rows={rows} {...(finalInputProps as any)} />}
            </>
          )}
        </>

        {inputOptions?.type === 'choose' && (
          <>
            {variant === 'text' && (
              <img
                alt="Toggle Menu"
                src={disabled ? downDisabledIcon : downIcon}
                className={classes.baseIcon}
                style={{
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  paddingRight: '0.25rem',
                  width: 26,
                  height: 41,
                }}
                onClick={disabled ? undefined : onOpenPopup}
              />
            )}
            {renderPopper(
              <SelectorItems
                selectedValue={field.decodeResult.success ? field.decodeResult.data : []}
                onSelectorOptionSelect={(value) => {
                  field.setOutput(value);
                  onSelect?.();
                  onClosePopup();
                }}
                selectorOptions={inputOptions?.options}
              />,
              'click',
            )}
          </>
        )}

        {inputOptions?.type === 'date' && (
          <>
            <img
              src={calendarSvg}
              className={clsx(!finalInputProps.disabled && classes.baseButton)}
              style={{ width: 16, height: 16, marginTop: 3, marginRight: 3 }}
              onClick={finalInputProps.disabled ? undefined : onOpenPopup}
            />
            <Dialog
              open={isPopupOpen}
              onClose={() => {
                setTimeout(() => {
                  onClosePopup();
                });
              }}
            >
              <KeyboardDatePicker
                disableToolbar
                variant="static"
                onChange={(d) => {
                  field.setOutput(d?.format('YYYY-MM-DD'));

                  requestIdleCallback(() => {
                    field.setTouched(true);
                  });

                  onSelect?.();
                  onClosePopup();
                }}
                value={
                  field.decodeResult.success
                    ? field.decodeResult.data
                    : dayjs().format('YYYY-MM-DD')
                }
                {...inputOptions.keyboardDatePickerProps}
              />
            </Dialog>
          </>
        )}

        {inputOptions?.type === 'password' && (
          <button type="button" onClick={() => setPasswordVisible((v) => !v)}>
            {passwordVisible ? (
              <VisibilityOffIcon className={classes.baseIcon} />
            ) : (
              <VisibilityIcon className={classes.baseIcon} />
            )}
          </button>
        )}

        {autoCompleteConfig &&
          renderPopper(
            <AutoCompleteItems field={field} config={autoCompleteConfig} onClose={onClosePopup} />,
            'focus',
          )}
      </div>
      {withErrorMessages && <ErrorMessages field={field} />}
    </>
  );
});
